import React, { useRef, useState } from 'react';
import { Button, Modal, message } from 'antd';
import { CheckOutlined, CloseOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import cardClient from '../../client/CardClient';
import useTranslation from '../../hooks/useTranslation';

/**
 * Редактирование карточки. Вопрос и ответ — тот же RichTextEditor, что и в форме
 * добавления: содержимое хранится состоянием редактора, поэтому берётся через ref,
 * а не из React-состояния.
 *
 * onSave получает уже сохранённую карточку — список обновляет строку у себя,
 * не перезагружая страницу.
 */
const EditCardForm = ({ card, deckId, onSave, onCancel, onDelete }) => {
    const { t } = useTranslation();
    const questionEditorRef = useRef(null);
    const answerEditorRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleSave = async () => {
        const question = questionEditorRef.current ? questionEditorRef.current.getContent() : '';
        const answer = answerEditorRef.current ? answerEditorRef.current.getContent() : '';

        if (!question.trim()) {
            message.error(t('card.questionRequired'));
            return;
        }
        if (!answer.trim()) {
            message.error(t('card.answerRequired'));
            return;
        }

        // deckId и содержимое — всё, что принимает бэкенд: id приходит из пути,
        // владельца он ставит из токена сам
        const cardData = { deckId, question, answer };

        setSaving(true);
        try {
            const updatedCard = await cardClient.updateCard(card.id, cardData);
            message.success(t('card.updated'));
            onSave?.(updatedCard ?? { ...card, ...cardData });
        } catch (error) {
            console.error('Error updating card:', error);
            message.error(t('card.updateFailed'));
        } finally {
            setSaving(false);
        }
    };

    // Подтверждение через Modal.confirm: удаление необратимо, а кнопка стоит рядом
    // с «Сохранить» — промах мышью не должен стоить карточки
    const handleDelete = () => {
        Modal.confirm({
            title: t('card.deleteConfirmTitle'),
            icon: <ExclamationCircleOutlined />,
            content: t('card.deleteConfirmBody'),
            okText: t('common.delete'),
            okButtonProps: { danger: true },
            cancelText: t('common.cancel'),
            onOk: async () => {
                setDeleting(true);
                try {
                    await cardClient.deleteCard(card.id);
                    message.success(t('card.deleted'));
                    onDelete?.(card.id);
                } catch (error) {
                    console.error('Error deleting card:', error);
                    message.error(t('card.deleteFailed'));
                } finally {
                    setDeleting(false);
                }
            },
        });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <RichTextEditor
                ref={questionEditorRef}
                placeholder={t('card.questionPlaceholder')}
                initialContent={card.question}
                minHeight={50}
            />
            <RichTextEditor
                ref={answerEditorRef}
                placeholder={t('card.answerPlaceholder')}
                initialContent={card.answer}
                minHeight={150}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                <Button danger icon={<DeleteOutlined />} loading={deleting} onClick={handleDelete}>
                    {t('common.delete')}
                </Button>
                <div style={{ display: 'flex', gap: '16px' }}>
                    <Button icon={<CloseOutlined />} onClick={onCancel}>
                        {t('common.close')}
                    </Button>
                    <Button type="primary" icon={<CheckOutlined />} loading={saving} onClick={handleSave}>
                        {t('common.save')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default EditCardForm;
